import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DateField from '../../components/common/DateField';
import { ktmToday } from '../../utils/calendar';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/common/ConfirmModal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';
import BeltBadge, { BELT_MAP } from '../../components/common/BeltBadge';
import EmptyState from '../../components/common/EmptyState';
import useEscapeKey from '../../hooks/useEscapeKey';
import useImpersonation from '../../hooks/useImpersonation';
import { getTrainers } from '../../services/trainerService';
import {
  getBeltExams, createBeltExam, getExamDetail, getEligibleStudents,
  addParticipants, removeParticipant, startExam, gradeStudent,
  finalizeExam, cancelExam,
} from '../../services/beltExamService';
import { BELT_ORDER } from '../../constants/belts';
import {
  MdAdd, MdEmojiEvents, MdClose, MdPeople, MdCheckCircle,
  MdArrowBack, MdPlayArrow, MdDoneAll, MdDelete, MdBlock,
  MdSearch, MdStar, MdPerson, MdExpandMore,
  MdCalendarToday, MdLocationOn,
} from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import useTheme from '../../context/ThemeContext';

// Status pill styles for the list cards (light chips that adapt in dark via the compat layer).
const STATUS_PILL_DARK = {
  scheduled:   'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-gray-100 text-muted',
};

// Dominant belt color for the card accent bar (stripe belts → first color via `bg`).
const beltAccent = (belt) => BELT_MAP[belt]?.bg || '#64748b';

// One status-aware primary action per card. All open the existing detail view
// (there is no distinct list-level grade/participants handler).
const examCardAction = (exam) => {
  const n = exam.participants?.length || 0;
  switch (exam.status) {
    case 'scheduled':   return n === 0
      ? { label: 'Add participants', tone: 'rose' }
      : { label: 'Manage', tone: 'bordered' };
    case 'in_progress': return { label: 'Grade now', tone: 'rose' };
    case 'completed':   return { label: 'View results', tone: 'bordered' };
    case 'cancelled':   return { label: 'View', tone: 'muted' };
    default:            return { label: 'View', tone: 'bordered' };
  }
};

const ACTION_TONE = {
  rose:     'bg-secondary text-card hover:opacity-90',
  bordered: 'border border-divider text-secondary hover:bg-nav-hover',
  muted:    'text-muted hover:text-secondary',
};

const EXAM_SORT_OPTIONS = [
  { value: 'date:desc',      label: 'Exam date (newest)' },
  { value: 'date:asc',       label: 'Exam date (oldest)' },
  { value: 'createdAt:desc', label: 'Created (newest)' },
  { value: 'createdAt:asc',  label: 'Created (oldest)' },
  { value: 'title:asc',      label: 'Title (A–Z)' },
  { value: 'status:asc',     label: 'Status (A–Z)' },
];

const STATUS_LABEL = {
  scheduled:   'Scheduled',
  in_progress: 'In Progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
};

const RESULT_STYLE = {
  promoted: 'bg-green-100 text-green-700',
  failed:   'bg-red-100 text-red-600',
  held:     'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  pending:  'bg-nav-hover text-muted',
};

const SCORE_CATEGORIES = ['poomsae', 'sparring', 'boardBreaking', 'theory', 'attitude'];
const SCORE_LABELS = {
  poomsae:       'Poomsae / Forms',
  sparring:      'Sparring',
  boardBreaking: 'Board Breaking',
  theory:        'Theory / Knowledge',
  attitude:      'Attitude / Spirit',
};

function StudentAvatar({ student, size = 'md' }) {
  const dim = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const name = student?.fullName || student?.user?.name || '';
  const initials = name ? name[0].toUpperCase() : '?';
  return (
    <div className={`${dim} bg-primary-light text-primary rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

// ── Schedule Exam Modal ───────────────────────────────────────────────────────
function CreateExamModal({ onClose, onSuccess, defaultBelt = '' }) {
  const { isReadOnly } = useImpersonation();
  useEscapeKey(onClose);
  const { register, control, handleSubmit, watch, formState: { errors } } = useForm({
    // default today in Kathmandu (AD ISO, future allowed); targetBeltLevel is pre-filled when
    // opened via the Grading page's "Schedule exam" deep link (?createFor=<belt>).
    defaultValues: { date: ktmToday(), targetBeltLevel: defaultBelt },
  });
  const [serverError, setServerError] = useState('');
  const selectedBelt = watch('targetBeltLevel');

  const { data: trainerData } = useQuery({
    queryKey: ['trainers-list'],
    queryFn:  () => getTrainers({ limit: 100, isActive: 'true' }).then(r => r.data.trainers),
  });

  const mutation = useMutation({
    mutationFn: (data) => createBeltExam({ ...data, examiner: data.examiner || undefined }),
    onSuccess: (res) => {
      toast.success('Belt exam scheduled');
      onSuccess(res.data.exam._id);
    },
    onError: (err) => setServerError(err.response?.data?.message || 'Failed to schedule exam'),
  });

  const selectCls = 'w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* Bounded height + internal scroll so the footer buttons are always visible; mobile sheet */}
      <div className="bg-card border border-divider rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-divider flex-shrink-0">
          <div>
            <h2 className="font-bold text-secondary text-base">Schedule belt exam</h2>
            <p className="text-xs text-muted mt-0.5">Set a future grading date and the belt being tested for.</p>
          </div>
          <button onClick={onClose} className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover transition-colors flex-shrink-0">
            <MdClose className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {serverError && (
              <div className="bg-red-500/10 text-red-500 text-sm rounded-xl px-4 py-3">{serverError}</div>
            )}

            {/* Title */}
            <Input
              label="Exam title *"
              placeholder="e.g. Yellow Belt Grading — April 2025"
              {...register('title', { required: 'Title is required' })}
              error={errors.title?.message}
            />

            {/* Target belt — with a belt-color dot reflecting the selection */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Target belt *</label>
              <div className="relative">
                {selectedBelt && (
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border border-divider pointer-events-none"
                    style={{ background: beltAccent(selectedBelt) }}
                  />
                )}
                <select
                  {...register('targetBeltLevel', { required: 'Target belt is required' })}
                  className={`${selectCls} min-h-[44px] ${selectedBelt ? 'pl-8' : ''}`}
                >
                  <option value="">Select belt</option>
                  {BELT_ORDER.filter(b => b !== 'White').map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              {errors.targetBeltLevel && <p className="text-xs text-red-500 mt-1">{errors.targetBeltLevel.message}</p>}
            </div>

            {/* Exam date — compact, tap-to-open (closed by default, no giant inline calendar) */}
            <Controller
              name="date"
              control={control}
              rules={{ required: 'Date is required' }}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Exam date *</label>
                  <DateField
                    label="Exam date"
                    placeholder="Select exam date"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                  {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date.message}</p>}
                </div>
              )}
            />

            {/* Examiner + Venue side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Examiner <span className="text-muted font-normal">(optional)</span></label>
                <select {...register('examiner')} className={`${selectCls} min-h-[44px]`}>
                  <option value="">None</option>
                  {trainerData?.map(t => (
                    <option key={t._id} value={t.user?._id || t._id}>{t.fullName}</option>
                  ))}
                </select>
              </div>
              <Input label="Venue" placeholder="e.g. Main Dojo" {...register('venue')} />
            </div>

            {/* Notes */}
            <Input label="Notes" placeholder="Optional notes…" {...register('notes')} />
          </div>

          {/* Sticky footer — always visible */}
          <div className="flex justify-end gap-2 px-6 py-3 border-t border-divider flex-shrink-0">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            >Schedule exam</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Grade Student Modal ───────────────────────────────────────────────────────
function GradeModal({ examId, participant, onClose, onSaved }) {
  const { isReadOnly } = useImpersonation();
  useEscapeKey(onClose);
  const studentName = participant?.student?.fullName
    || participant?.student?.user?.name
    || 'Unknown Student';
  const memberId = participant?.student?.platformStudentId || participant?.student?.studentId || '';
  const currentGrade = participant?.currentGrade || '—';
  const targetGrade  = participant?.targetGrade  || '—';

  const [scores, setScores] = useState(
    SCORE_CATEGORIES.reduce((acc, cat) => {
      acc[cat] = participant?.scores?.[cat] != null ? String(participant.scores[cat]) : '';
      return acc;
    }, {})
  );
  const [selectedResult, setSelectedResult] = useState(
    participant?.result && participant.result !== 'pending' ? participant.result : null
  );
  const [notes, setNotes] = useState(participant?.examinerNotes || '');
  const [error, setError] = useState('');

  const allFilled = SCORE_CATEGORIES.every(
    cat => scores[cat] !== '' && !isNaN(Number(scores[cat]))
  );

  const overall = allFilled
    ? parseFloat((SCORE_CATEGORIES.reduce((s, cat) => s + Number(scores[cat]), 0) / SCORE_CATEGORIES.length).toFixed(2))
    : null;

  const suggestedResult = overall === null ? null
    : overall >= 7   ? 'promoted'
    : overall >= 5   ? 'held'
    : 'failed';

  const canSave = allFilled && selectedResult !== null;

  const mutation = useMutation({
    mutationFn: () => gradeStudent(examId, {
      studentId:     participant.student._id,
      scores:        Object.fromEntries(
        SCORE_CATEGORIES.map(cat => [cat, parseFloat(scores[cat])])
      ),
      examinerNotes: notes,
      result:        selectedResult,
    }),
    onSuccess: () => {
      toast.success('Grades saved');
      onSaved();
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to save grades'),
  });

  // Selected state reuses the soft result-pill tokens (bg-*-100/text-*) + a colored
  // border, so a chosen decision reads as a calm tinted chip — not a bright block —
  // and stays muted in dark mode (the dark-compat layer leaves these *-100 chips alone).
  const resultOptions = [
    { value: 'promoted', label: 'Promoted', icon: '✅', selected: 'bg-green-100 text-green-700 border-green-400' },
    { value: 'held',     label: 'Held',     icon: '⏸',  selected: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
    { value: 'failed',   label: 'Failed',   icon: '❌', selected: 'bg-red-100 text-red-600 border-red-400' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-divider flex-shrink-0">
          <StudentAvatar student={participant?.student} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-secondary text-sm leading-tight">{studentName}</p>
            {memberId && <p className="text-xs text-subtle">{memberId}</p>}
            <div className="flex items-center gap-1.5 mt-1">
              <BeltBadge belt={currentGrade} size="xs" />
              <span className="text-subtle text-xs">→</span>
              <BeltBadge belt={targetGrade} size="xs" />
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover flex-shrink-0">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2">{error}</div>}

          {/* Score inputs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted uppercase tracking-wide">Scores</p>
              <p className="text-xs text-subtle">Pass threshold: <span className="font-semibold text-muted">6 / 10</span></p>
            </div>
            <div className="space-y-2">
              {SCORE_CATEGORIES.map(cat => {
                const v = scores[cat];
                const num = parseFloat(v);
                const filled = v !== '' && !isNaN(num);
                const passing = filled && num >= 6;
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <span className="text-sm text-muted flex-1">{SCORE_LABELS[cat]}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.5"
                        value={v}
                        onChange={e => setScores(prev => ({ ...prev, [cat]: e.target.value }))}
                        placeholder="0–10"
                        className={`w-16 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 transition-colors ${
                          filled
                            ? passing
                              ? 'border-green-300 bg-green-50 text-green-800 focus:ring-green-200'
                              : 'border-red-300 bg-red-50 text-red-800 focus:ring-red-200'
                            : 'border-divider focus:ring-primary/30'
                        }`}
                      />
                      <span className={`text-xs font-medium w-10 ${
                        !filled ? 'text-subtle' : passing ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {!filled ? '—' : passing ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Overall score */}
          {overall !== null && (
            <div className={`rounded-xl px-4 py-3 ${overall >= 6 ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-secondary">Overall Score</span>
                <span className={`text-xl font-bold ${overall >= 6 ? 'text-green-700' : 'text-red-600'}`}>
                  {overall} <span className="text-sm font-normal text-subtle">/ 10</span>
                </span>
              </div>
              {suggestedResult && (
                <p className="text-xs text-muted mt-1">
                  Suggested: <span className={`font-semibold capitalize ${
                    suggestedResult === 'promoted' ? 'text-green-700' :
                    suggestedResult === 'held'     ? 'text-amber-700' : 'text-red-600'
                  }`}>{suggestedResult}</span>
                </p>
              )}
            </div>
          )}

          {/* Result selector */}
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Decision *</p>
            <div className="flex gap-2">
              {resultOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedResult(opt.value)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                    selectedResult === opt.value
                      ? opt.selected
                      : 'bg-card text-muted border-divider hover:bg-nav-hover'
                  }`}
                >
                  <span className="block text-base mb-0.5">{opt.icon}</span>
                  {opt.label}
                  {suggestedResult === opt.value && selectedResult !== opt.value && (
                    <span className="block text-[9px] text-subtle mt-0.5">Suggested</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Examiner notes */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Examiner Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Private notes about this student's performance…"
              className="w-full bg-card border border-divider rounded-xl px-3 py-2 text-sm text-secondary placeholder-subtle focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSave || mutation.isPending || isReadOnly}
            loading={mutation.isPending}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          >
            Save Scores
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Finalize Confirmation Modal ───────────────────────────────────────────────
function FinalizeModal({ exam, onClose, onFinalized }) {
  const { isReadOnly } = useImpersonation();
  useEscapeKey(onClose);
  const promoted = exam.participants.filter(p => p.result === 'promoted');
  const held     = exam.participants.filter(p => p.result === 'held');
  const failed   = exam.participants.filter(p => p.result === 'failed');

  const mutation = useMutation({
    mutationFn: () => finalizeExam(exam._id),
    onSuccess: () => {
      toast.success(`Exam finalized — ${promoted.length} student${promoted.length !== 1 ? 's' : ''} promoted!`);
      onFinalized();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to finalize'),
  });

  const getName = (p) => p.student?.fullName || p.student?.user?.name || 'Unknown';
  const getScore = (p) => p.overallScore != null ? `Score: ${p.overallScore}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary text-sm">Confirm Finalize</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-muted">
            The following belt changes will be made <strong>permanently</strong>. This cannot be undone.
          </p>

          {promoted.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                <MdCheckCircle className="text-green-600" /> Promoted — belt will be upgraded ({promoted.length})
              </p>
              <div className="space-y-2">
                {promoted.map((p) => (
                  <div key={p._id} className="flex items-center gap-2 p-2.5 bg-green-50 rounded-xl">
                    <StudentAvatar student={p.student} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">{getName(p)}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <BeltBadge belt={p.currentGrade} size="xs" />
                        <span className="text-subtle text-xs">→</span>
                        <BeltBadge belt={p.targetGrade || exam.targetBeltLevel} size="xs" />
                      </div>
                    </div>
                    {getScore(p) && <span className="text-xs text-green-700 font-semibold">{getScore(p)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {held.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                ⏸ Held — no belt change ({held.length})
              </p>
              <div className="space-y-2">
                {held.map((p) => (
                  <div key={p._id} className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-xl">
                    <StudentAvatar student={p.student} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">{getName(p)}</p>
                      <p className="text-xs text-subtle">Stays at {p.currentGrade}</p>
                    </div>
                    {getScore(p) && <span className="text-xs text-amber-700 font-semibold">{getScore(p)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {failed.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                ❌ Failed — no belt change ({failed.length})
              </p>
              <div className="space-y-2">
                {failed.map((p) => (
                  <div key={p._id} className="flex items-center gap-2 p-2.5 bg-red-50 rounded-xl">
                    <StudentAvatar student={p.student} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">{getName(p)}</p>
                      <p className="text-xs text-subtle">Stays at {p.currentGrade}</p>
                    </div>
                    {getScore(p) && <span className="text-xs text-red-600 font-semibold">{getScore(p)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          >
            <MdDoneAll className="text-base" />
            Finalize &amp; promote belts
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Add Participants Modal ────────────────────────────────────────────────────
function AddParticipantsModal({ exam, onClose, onAdded }) {
  const { isReadOnly } = useImpersonation();
  useEscapeKey(onClose);
  const [selected, setSelected] = useState([]);
  const [search, setSearch]     = useState('');

  const { data: eligibleData, isPending } = useQuery({
    queryKey: ['eligible', exam.targetBeltLevel],
    queryFn:  () => getEligibleStudents(exam.targetBeltLevel).then(r => r.data),
  });

  const mutation = useMutation({
    mutationFn: () => addParticipants(exam._id, selected),
    onSuccess: () => {
      toast.success('Participants added');
      onAdded();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add participants'),
  });

  const alreadyAdded = new Set(exam.participants.map(p => p.student?._id?.toString() || p.student?.toString()));
  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  const students = (eligibleData?.students || []).filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.fullName || '').toLowerCase().includes(q) ||
      (s.studentId || '').toLowerCase().includes(q) ||
      (s.platformStudentId || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <div>
            <h2 className="font-bold text-secondary text-sm">Add Participants</h2>
            <p className="text-xs text-subtle">
              {eligibleData?.prerequisiteBelt
                ? `Must hold: ${eligibleData.prerequisiteBelt} belt`
                : `Eligible for ${exam.targetBeltLevel}`}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 pb-2 flex-shrink-0">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-lg" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or ID…"
              className="w-full pl-9 pr-3 py-2 bg-card border border-divider rounded-xl text-sm text-secondary placeholder-subtle focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-3">
          {isPending ? (
            <div className="h-20 bg-nav-hover animate-pulse rounded-xl mt-2" />
          ) : !eligibleData?.students?.length ? (
            <div className="text-center py-8">
              <MdPeople className="text-4xl text-subtle mx-auto mb-2" />
              <p className="text-sm text-subtle">
                {eligibleData?.prerequisiteBelt
                  ? `No active students with ${eligibleData.prerequisiteBelt} belt found.`
                  : `No eligible students found for this belt.`}
              </p>
            </div>
          ) : !students.length ? (
            <p className="text-sm text-subtle text-center py-8">No results for "{search}"</p>
          ) : (
            <div className="space-y-2 mt-1">
              {students.map(s => {
                const added = alreadyAdded.has(s._id);
                return (
                  <label
                    key={s._id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      added
                        ? 'bg-nav-hover border-divider opacity-50 cursor-not-allowed'
                        : selected.includes(s._id)
                          ? 'bg-primary-light border-primary/20'
                          : 'border-divider hover:bg-nav-hover'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={added}
                      checked={selected.includes(s._id) || added}
                      onChange={() => !added && toggle(s._id)}
                      className="accent-primary flex-shrink-0"
                    />
                    <StudentAvatar student={s} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">{s.fullName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {(s.platformStudentId || s.studentId) && (
                          <span className="text-xs text-subtle">{s.platformStudentId || s.studentId}</span>
                        )}
                        <BeltBadge belt={s.currentGrade} size="xs" />
                        {s.bucket && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            s.bucket === 'eligible' ? 'bg-green-100 text-green-700'
                            : s.bucket === 'close'  ? 'bg-amber-100 text-amber-700'
                            : s.bucket === 'notYet' ? 'bg-gray-100 text-muted'
                            : 'bg-gray-100 text-subtle'
                          }`} title={
                            s.bucket === 'unknown' ? 'No promotion date on file'
                              : `${s.monthsInGrade} months, ${s.sessionsSinceGrading} sessions`
                          }>
                            {s.bucket === 'eligible' ? 'Eligible'
                              : s.bucket === 'close'  ? 'Close'
                              : s.bucket === 'notYet' ? 'Not yet'
                              : 'Unknown'}
                          </span>
                        )}
                      </div>
                    </div>
                    {added && <span className="text-xs text-green-600 font-medium flex-shrink-0">Added</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-divider flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-subtle">
            {selected.length > 0 ? `${selected.length} selected` : 'Select students to add'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!selected.length || mutation.isPending || isReadOnly}
              loading={mutation.isPending}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            >
              Add {selected.length > 0 ? selected.length : ''} Student{selected.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Success Screen (after finalize) ──────────────────────────────────────────
function FinalizeSuccessScreen({ exam, onViewResults }) {
  const promoted = exam.participants.filter(p => p.result === 'promoted');
  return (
    <Card>
      <div className="flex flex-col items-center text-center py-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <MdEmojiEvents className="text-4xl text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-secondary mb-1">Exam Complete!</h2>
        <p className="text-muted text-sm mb-6">
          {promoted.length} student{promoted.length !== 1 ? 's' : ''} promoted to their new belt.
        </p>
        {promoted.length > 0 && (
          <div className="w-full max-w-xs space-y-2 mb-6">
            {promoted.map(p => (
              <div key={p._id} className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <StudentAvatar student={p.student} size="sm" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-secondary">
                    {p.student?.fullName || p.student?.user?.name || 'Unknown'}
                  </p>
                </div>
                <BeltBadge belt={p.promotedGrade || p.targetGrade || exam.targetBeltLevel} size="xs" />
                <MdStar className="text-yellow-400 text-base" />
              </div>
            ))}
          </div>
        )}
        <Button onClick={onViewResults}>View Full Results</Button>
      </div>
    </Card>
  );
}

// ── Exam Detail View ──────────────────────────────────────────────────────────
function ExamDetail({ examId, onBack }) {
  document.title = 'Belt Exam — TrainAxis';
  const currentTheme = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const [confirm, confirmModal] = useConfirm();
  const queryClient = useQueryClient();
  const { formatDate } = useCalendar();
  const [gradingParticipant,   setGradingParticipant]   = useState(null);
  const [showFinalize,         setShowFinalize]          = useState(false);
  const [showAddParticipants,  setShowAddParticipants]   = useState(false);
  const [showSuccessScreen,    setShowSuccessScreen]     = useState(false);

  const { data: exam, isPending, refetch } = useQuery({
    queryKey: ['belt-exam-detail', examId],
    queryFn:  () => getExamDetail(examId).then(r => r.data.exam),
  });

  const startMutation = useMutation({
    mutationFn: () => startExam(examId),
    onSuccess: () => { toast.success('Exam started'); refetch(); },
    onError:   (err) => toast.error(err.response?.data?.message || 'Failed to start exam'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelExam(examId),
    onSuccess: () => {
      toast.success('Exam cancelled');
      queryClient.invalidateQueries({ queryKey: ['belt-exams'] });
      onBack();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to cancel exam'),
  });

  const removeMutation = useMutation({
    mutationFn: (studentId) => removeParticipant(examId, studentId),
    onSuccess: () => { toast.success('Participant removed'); refetch(); },
    onError:   (err) => toast.error(err.response?.data?.message || 'Failed to remove'),
  });

  if (isPending) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-nav-hover animate-pulse rounded-xl" />
        <div className="h-40 bg-nav-hover animate-pulse rounded-2xl" />
        <div className="h-64 bg-nav-hover animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="text-center py-16 text-subtle">
        <p>Exam not found.</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">Back to Exams</Button>
      </div>
    );
  }

  const pendingCount   = exam.participants.filter(p => p.result === 'pending').length;
  const gradedCount    = exam.participants.length - pendingCount;
  const canStart       = exam.status === 'scheduled' && exam.participants.length > 0;
  const canAddParts    = ['scheduled', 'in_progress'].includes(exam.status);

  // Get the student ID to pass to remove/grade — use the populated _id or the raw ObjectId
  const getStudentId = (p) => p.student?._id || p.student;

  const statusPill = {
    scheduled:   'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    completed:   'bg-green-100 text-green-700',
    cancelled:   'bg-nav-hover text-muted',
  }[exam.status] || 'bg-nav-hover text-muted';
  const showScores = exam.status !== 'scheduled';

  // Compact, theme-aware row buttons (reused per participant).
  const rowGrade  = `inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200`;
  const rowEdit   = 'inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 bg-card text-secondary border border-divider hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200';
  const rowRemove = 'inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 border border-red-200 text-red-500 hover:bg-nav-hover transition-all duration-200 disabled:opacity-50';
  // Destructive header button, sized to sit beside the shared <Button>.
  const cancelExamBtn = 'inline-flex items-center justify-center gap-2 font-medium rounded-full text-sm py-3 px-5 border border-red-200 text-red-500 hover:bg-nav-hover transition-all duration-200 disabled:opacity-60';

  return (
    <div className="space-y-4">
      {confirmModal}
      {/* Back link */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-secondary transition-colors"
      >
        <MdArrowBack size={16} /> All exams
      </button>

      {/* Header card */}
      <div className="bg-card border border-divider rounded-lg p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-secondary leading-snug">{exam.title}</h1>
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap mt-2 text-xs">
              <span className="flex items-center gap-1 text-muted"><MdCalendarToday size={13} /> <DualDate date={exam.date} /></span>
              <BeltBadge belt={exam.targetBeltLevel} size="xs" />
              <span className="flex items-center gap-1 text-muted">
                <MdPerson size={14} /> {exam.examiner?.name || <span className="text-subtle">No examiner set</span>}
              </span>
              <span className="flex items-center gap-1 text-muted">
                <MdLocationOn size={14} /> {exam.venue || <span className="text-subtle">No venue set</span>}
              </span>
            </div>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusPill}`}>
            {STATUS_LABEL[exam.status]}
          </span>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="bg-nav-hover rounded-lg px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-secondary leading-none">{exam.participants.length}</p>
            <p className="text-[11px] text-muted mt-1">Participants</p>
          </div>
          <div className="bg-nav-hover rounded-lg px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-green-600 leading-none">{gradedCount}</p>
            <p className="text-[11px] text-muted mt-1">Graded</p>
          </div>
          <div className="bg-nav-hover rounded-lg px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-amber-600 leading-none">{pendingCount}</p>
            <p className="text-[11px] text-muted mt-1">Pending</p>
          </div>
        </div>

        {/* Status-aware actions */}
        {['scheduled', 'in_progress'].includes(exam.status) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {canAddParts && (
              <Button
                variant="ghost"
                onClick={() => setShowAddParticipants(true)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              >
                <MdPeople className="text-base" /> Add students
              </Button>
            )}
            {exam.status === 'scheduled' && (
              <Button
                onClick={() => startMutation.mutate()}
                disabled={!canStart || startMutation.isPending || isReadOnly}
                loading={startMutation.isPending}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              >
                <MdPlayArrow className="text-base" /> Start exam
              </Button>
            )}
            <button
              onClick={async () => { if (await confirm({ title: 'Cancel exam?', message: 'Cancel this exam? This cannot be undone.', confirmLabel: 'Cancel exam' })) cancelMutation.mutate(); }}
              disabled={cancelMutation.isPending || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className={`${cancelExamBtn} disabled:cursor-not-allowed`}
            >
              <MdBlock className="text-base" /> Cancel exam
            </button>
          </div>
        )}
      </div>

      {/* Hint: grading unlocks once the exam is started */}
      {exam.status === 'scheduled' && exam.participants.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <MdPlayArrow className="text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800">Start the exam to begin grading students.</p>
        </div>
      )}

      {/* Participants */}
      {exam.participants.length === 0 ? (
        <div className="bg-card border border-divider rounded-lg py-12 text-center">
          <MdPeople className="text-5xl text-subtle mx-auto mb-3" />
          <p className="text-muted text-sm mb-4">No participants yet</p>
          {canAddParts && (
            <Button
              onClick={() => setShowAddParticipants(true)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            >
              <MdAdd className="text-base" /> Add students
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {exam.participants.map((p) => {
            const name     = p.student?.fullName || p.student?.user?.name || '—';
            const memberId = p.student?.platformStudentId || p.student?.studentId || '';
            const sid      = getStudentId(p);
            const graded   = p.result !== 'pending';
            return (
              <div key={p._id} className="bg-card border border-divider rounded-lg p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Identity */}
                <div className="flex items-center gap-2.5 min-w-0 sm:flex-1">
                  <StudentAvatar student={p.student} size="sm" />
                  <div className="min-w-0">
                    <p className="font-medium text-secondary truncate">{name}</p>
                    {memberId && <p className="text-xs text-muted truncate">{memberId}</p>}
                  </div>
                </div>

                {/* Belt progression · score · result · action (stacks on mobile, never hidden) */}
                <div className="flex items-center justify-between sm:justify-end gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <BeltBadge belt={p.currentGrade} size="xs" />
                    <span className="text-subtle text-xs">→</span>
                    <BeltBadge belt={p.targetGrade || exam.targetBeltLevel} size="xs" />
                  </div>

                  {showScores && (
                    <div className="text-center min-w-[2.25rem]">
                      {p.overallScore != null
                        ? <span className={`text-sm font-bold ${p.overallScore >= 6 ? 'text-green-600' : 'text-red-600'}`}>{p.overallScore}</span>
                        : <span className="text-subtle text-sm">—</span>}
                    </div>
                  )}

                  {showScores && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${RESULT_STYLE[p.result] || RESULT_STYLE.pending}`}>
                      {p.result === 'pending' ? 'Not graded' : p.result.charAt(0).toUpperCase() + p.result.slice(1)}
                    </span>
                  )}

                  {['scheduled', 'in_progress'].includes(exam.status) && (
                    <div className="flex items-center gap-2">
                      {exam.status === 'in_progress' && (
                        <button
                          onClick={() => setGradingParticipant(p)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                          className={`${graded ? rowEdit : rowGrade} disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {graded ? 'Edit' : 'Grade'}
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          const n = p.student?.fullName || 'this student';
                          if (await confirm({ title: 'Remove participant?', message: `Remove ${n} from this exam?`, confirmLabel: 'Remove' })) removeMutation.mutate(sid);
                        }}
                        disabled={removeMutation.isPending || isReadOnly}
                        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                        className={`${rowRemove} disabled:cursor-not-allowed`}
                      >
                        <MdDelete size={14} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer state bar */}
      {exam.status === 'in_progress' && (
        pendingCount > 0 ? (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-800 flex items-center gap-1.5">
              <MdPlayArrow className="text-amber-500 flex-shrink-0" />
              {pendingCount} student{pendingCount !== 1 ? 's' : ''} still need grading before you can finalize.
            </p>
            <Button disabled><MdDoneAll className="text-base" /> Finalize &amp; promote</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <p className="text-sm font-medium text-green-700 flex items-center gap-1.5">
              <MdCheckCircle className="text-base flex-shrink-0" /> All students graded — ready to finalize.
            </p>
            <Button
              onClick={() => setShowFinalize(true)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            ><MdDoneAll className="text-base" /> Finalize &amp; promote</Button>
          </div>
        )
      )}
      {exam.status === 'completed' && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700 font-medium">
          <MdCheckCircle className="text-base flex-shrink-0" /> Exam finalized. All scores are locked.
        </div>
      )}

      {exam.notes && (
        <div className="bg-nav-hover rounded-lg px-4 py-3 text-sm text-muted">
          <span className="font-medium text-secondary">Notes:</span> {exam.notes}
        </div>
      )}

      {/* Modals */}
      {gradingParticipant && (
        <GradeModal
          examId={exam._id}
          participant={gradingParticipant}
          onClose={() => setGradingParticipant(null)}
          onSaved={() => {
            setGradingParticipant(null);
            refetch();
          }}
        />
      )}

      {showFinalize && (
        <FinalizeModal
          exam={exam}
          onClose={() => setShowFinalize(false)}
          onFinalized={() => {
            setShowFinalize(false);
            setShowSuccessScreen(true);
            queryClient.invalidateQueries({ queryKey: ['belt-exams'] });
            refetch();
          }}
        />
      )}

      {showSuccessScreen && exam.status === 'completed' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSuccessScreen(false)}>
          <div className="w-full max-w-md">
            <FinalizeSuccessScreen
              exam={exam}
              onViewResults={() => setShowSuccessScreen(false)}
            />
          </div>
        </div>
      )}

      {showAddParticipants && (
        <AddParticipantsModal
          exam={exam}
          onClose={() => setShowAddParticipants(false)}
          onAdded={() => {
            setShowAddParticipants(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Main List Page ────────────────────────────────────────────────────────────
function BeltExams() {
  document.title = 'Belt Exams — TrainAxis';
  const currentTheme = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const { formatDate } = useCalendar();
  const queryClient          = useQueryClient();
  const [statusFilter, setFilter] = useState('');
  const [showCreate,  setCreate]  = useState(false);
  // Detail view is driven by the ?examId= URL param (matches the Dashboard deep-links).
  // Opening an exam is a real navigation, so the browser Back button returns to the
  // list rather than the page the user came from.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeExamId = searchParams.get('examId');
  const openExam  = (id) => setSearchParams({ examId: id });
  const closeExam = () => setSearchParams({});

  // Deep-link from the Grading page ("Schedule exam" for a group of eligible students) —
  // ?createFor=<targetBelt> auto-opens the create modal pre-filled with that belt. Read once on
  // mount, then stripped from the URL so refreshing/reopening doesn't re-trigger it.
  const [prefillBelt, setPrefillBelt] = useState('');
  useEffect(() => {
    const createFor = searchParams.get('createFor');
    if (createFor) {
      setPrefillBelt(createFor);
      setCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete('createFor');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination / sorting / search
  const [page, setPage]                 = useState(1);
  const [limit, setLimit]               = useState(10);
  const [sortBy, setSortBy]             = useState('date');
  const [sortOrder, setSortOrder]       = useState('desc');
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebounced] = useState('');
  const searchTimer = useRef(null);

  const { data, isPending } = useQuery({
    queryKey: ['belt-exams', statusFilter, page, limit, sortBy, sortOrder, debouncedSearch],
    queryFn:  () => {
      const params = { page, limit, sortBy, sortOrder };
      if (statusFilter)    params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      return getBeltExams(params).then(r => r.data);
    },
    enabled:  !activeExamId,
    placeholderData: keepPreviousData,
  });

  const exams  = data?.exams || data?.data || [];
  const total  = data?.pagination?.total ?? exams.length;
  const pages  = data?.pagination?.totalPages ?? 1;
  const counts = data?.statusCounts || { scheduled: 0, in_progress: 0, completed: 0, cancelled: 0, total: 0 };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebounced(val); setPage(1); }, 400);
  };

  // If an action empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && exams.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, exams.length, page]);

  if (activeExamId) {
    return (
      <ExamDetail
        examId={activeExamId}
        onBack={() => {
          closeExam();
          queryClient.invalidateQueries({ queryKey: ['belt-exams'] });
        }}
      />
    );
  }

  return (
    <div>
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total',       value: counts.total,       text: 'text-secondary' },
          { label: 'Scheduled',   value: counts.scheduled,   text: 'text-blue-600'  },
          { label: 'In progress', value: counts.in_progress, text: 'text-amber-600' },
          { label: 'Completed',   value: counts.completed,   text: 'text-green-600' },
        ].map(({ label, value, text }) => (
          <div key={label} className="bg-card border border-divider rounded-xl p-3.5">
            <p className="text-xs text-muted font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${text}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter + create */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            ['', 'All'],
            ['scheduled',   'Scheduled'],
            ['in_progress', 'In progress'],
            ['completed',   'Completed'],
            ['cancelled',   'Cancelled'],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => { setFilter(val); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                statusFilter === val
                  ? 'bg-secondary text-card border-secondary'
                  : 'bg-card text-muted border-divider hover:text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCreate(true)}
          disabled={isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          className={`flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <MdAdd className="text-lg" /> Schedule exam
        </button>
      </div>

      {/* Search · sort · rows per page */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search exam title…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-full bg-card border border-divider text-secondary placeholder-subtle focus:outline-none focus:border-secondary transition-colors"
          />
        </div>
        <div className="relative min-w-[170px]">
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={e => { const [b, o] = e.target.value.split(':'); setSortBy(b); setSortOrder(o); setPage(1); }}
            className="w-full appearance-none rounded-full pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary"
          >
            {EXAM_SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
        </div>
        <div className="relative min-w-[110px]">
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            className="w-full appearance-none rounded-full pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary"
            title="Rows per page"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
        </div>
      </div>

      {/* Exam cards */}
      {isPending ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[200px] bg-card border border-divider animate-pulse rounded-xl" />
          ))}
        </div>
      ) : !exams.length ? (
        <Card>
          <EmptyState
            icon={MdEmojiEvents}
            title="No belt exams yet"
            description="Schedule a grading exam to start tracking student ranks and belt progression."
            primaryAction={{ label: 'Schedule Exam', icon: MdAdd, onClick: () => { if (!isReadOnly) setCreate(true); } }}
            secondaryAction={{ label: 'View Students', icon: MdPerson, to: '/admin/students' }}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {exams.map(exam => {
            const n      = exam.participants?.length || 0;
            const action = examCardAction(exam);
            return (
              <div
                key={exam._id}
                onClick={() => openExam(exam._id)}
                className="relative flex flex-col min-h-[200px] rounded-xl border border-divider bg-card overflow-hidden cursor-pointer hover:bg-nav-hover transition-colors"
              >
                {/* Belt-colored accent bar */}
                <span
                  className="absolute left-0 top-0 bottom-0 w-[3px]"
                  style={{ background: beltAccent(exam.targetBeltLevel) }}
                />

                <div className="flex flex-col flex-1 p-3.5 pl-4">
                  {/* Title + status */}
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="font-medium text-secondary text-sm leading-snug"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {exam.title}
                    </p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_PILL_DARK[exam.status]}`}>
                      {STATUS_LABEL[exam.status]}
                    </span>
                  </div>

                  {/* Meta: BS date + belt chip */}
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="flex items-center gap-1 text-xs text-muted">
                      <MdCalendarToday size={13} /> <DualDate date={exam.date} />
                    </span>
                    <BeltBadge belt={exam.targetBeltLevel} size="xs" />
                  </div>

                  {/* Detail rows (always shown for equal height) */}
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs">
                      <MdPerson size={14} className="text-muted flex-shrink-0" />
                      {exam.examiner?.name
                        ? <span className="text-secondary truncate">{exam.examiner.name}</span>
                        : <span className="text-subtle">No examiner set</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <MdLocationOn size={14} className="text-muted flex-shrink-0" />
                      {exam.venue
                        ? <span className="text-secondary truncate">{exam.venue}</span>
                        : <span className="text-subtle">No venue set</span>}
                    </div>
                  </div>

                  {/* Bottom row (pinned) */}
                  <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-divider">
                    <span className="flex items-center gap-1.5 text-xs text-muted">
                      <MdPeople size={14} /> {n} participant{n !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); openExam(exam._id); }}
                      className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-200 ${
                        action.tone === 'rose'
                          ? `${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90`
                          : ACTION_TONE[action.tone]
                      }`}
                    >
                      {action.label}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-4 bg-card border border-divider rounded-2xl">
          <Pagination page={page} pages={pages} total={total} limit={limit} onChange={(p) => setPage(p)} />
        </div>
      )}

      {showCreate && (
        <CreateExamModal
          defaultBelt={prefillBelt}
          onClose={() => { setCreate(false); setPrefillBelt(''); }}
          onSuccess={(examId) => {
            setCreate(false);
            setPrefillBelt('');
            queryClient.invalidateQueries({ queryKey: ['belt-exams'] });
            openExam(examId);
          }}
        />
      )}
    </div>
  );
}

export default BeltExams;
